var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import * as userAgent from "@play-co/user-agent";
import { analytics } from "./analytics/index.mjs";
import { delay } from "./asyncUtils.mjs";
import { createDom } from "./domUtils.mjs";
import { createError } from "./errorUtils.mjs";
import { Friend } from "./Friend.mjs";
import getQueryParameters from "./getQueryParameters.mjs";
import globalTime from "./globalTime.mjs";
import mockConfig from "./mock/config.mjs";
import contextSwitcher from "./mock/contextSwitcher.mjs";
import friendPicker from "./mock/friendPicker.mjs";
import matchmakingInfo from "./mock/matchmakingInfo.mjs";
import matchmakingSearch from "./mock/matchmakingSearch.mjs";
import purchaseConfirmation from "./mock/purchaseConfirmation.mjs";
import shareAsyncDialog from "./mock/shareAsyncDialog.mjs";
import squads from "./mock/squads.mjs";
import styles from "./mock/styles.mjs";
import switchAsync from "./mock/switchAsync.mjs";
import { createLegacyNativeInterface } from "./native/legacy/LegacyNativeInterface.mjs";
import { Platform, getLocalStorageData, getOrSetLocalStorageData } from "./Platform.mjs";
import { purchaseFromLocalCurrencyPurchase, localCurrencyPurchaseFromFacebookData } from "./Purchase.mjs";
import { SignedPlayerInfo } from "./SignedPlayerInfo.mjs";
import { unreachable } from "./typeUtils.mjs";
import { VirtualContext } from "./VirtualContext.mjs";
const DEFAULT_PLAYER_PHOTO_URL = "https://storage.googleapis.com/devkit-instant/avatar/animal_0001.png";
const LOCAL_STORAGE_PLAYER_ID_KEY = "playerID";
function getNewID() {
  return Math.floor(1e3 + Math.random() * 1e4).toString();
}
function getPlayerId() {
  const queryParams = getQueryParameters();
  if (queryParams.profile) {
    return queryParams.profile;
  }
  return getOrSetLocalStorageData(
    LOCAL_STORAGE_PLAYER_ID_KEY,
    getNewID()
  );
}
class PlatformMock extends Platform {
  constructor(opts) {
    super("mock");
    __publicField(this, "_context", null);
    this.legacyNative = (opts == null ? void 0 : opts.legacyNativeInterface) ?? createLegacyNativeInterface();
    this.supportedAPIs = {
      isContextSizeBetween: true,
      chooseContextAsync: true,
      createContextAsync: true,
      createShortcutAsync: false,
      canSubscribeBotAsync: false,
      subscribeBotAsync: false,
      flushDataAsync: true,
      filterFriendsAsync: true,
      getDataAsync: true,
      getEntryPointAsync: false,
      getEntryPointData: false,
      getFriendsAsync: true,
      getInterstitialAdAsync: false,
      getPlayersAsync: true,
      getRewardedVideoAsync: false,
      getSignedPlayerInfoAsync: true,
      getLeaderboardAsync: true,
      initializeAsync: true,
      postSessionScoreAsync: true,
      getTournamentAsync: true,
      createTournamentAsync: true,
      shareTournamentAsync: true,
      postTournamentScoreAsync: true,
      setDataAsync: true,
      setSessionData: true,
      shareAsync: true,
      // startGameAsync: true,
      switchContextAsync: true,
      switchGameAsync: false,
      checkCanPlayerMatchAsync: true,
      matchPlayerAsync: true,
      updateAsync: false,
      getCatalogAsync: true,
      purchaseAsync: true
    };
    const styleTag = createDom("style", void 0, document.head);
    styleTag.appendChild(document.createTextNode(styles));
  }
  async initializeAsync(config) {
    var _a, _b;
    if (this._initialization !== null) {
      return this._initialization;
    }
    const playerID = getPlayerId();
    this.readConfiguration(config);
    analytics.initialize({
      ...this._gameConfig,
      appID: this.appID,
      // this is to work around years-old technical debt related to appID sometimes being an object in the config
      convertCurrency: (...args) => this.convertCurrency(...args)
    });
    this._initialization = this._initialize(playerID);
    await this._initialization;
    if (playerID in mockConfig.player) {
      this.playerName = mockConfig.player[playerID].name;
      this.playerPhoto = mockConfig.player[playerID].photo;
    } else {
      this.playerName = "Test Name " + this.playerID;
      this.playerPhoto = DEFAULT_PLAYER_PHOTO_URL;
    }
    this.entryData = this._getQueryEntryData();
    this.entryData.payload = this.entryData.payload || {};
    this.osType = ((_a = this.legacyNative) == null ? void 0 : _a.platform.os) === "android" ? "ANDROID_APP" : ((_b = this.legacyNative) == null ? void 0 : _b.platform.os) === "ios" ? "IOS_APP" : userAgent.os.isAndroid ? "ANDROID" : userAgent.os.isIOS ? "IOS" : "WEB";
    this.isMobile = this.osType !== "WEB";
    this.insideNativeIOS = !!this.legacyNative || this.entryData._native_enabled;
    this.canPlayerMatch = true;
    this._isPaymentsReady = true;
    if (this._onPaymentsReady) {
      this._onPaymentsReady();
      this._onPaymentsReady = null;
    }
  }
  updateContext(contextAPI, context) {
    this._context = context;
    super._updateContext(contextAPI, {
      getID: () => context.id,
      getType: () => context.type
    });
  }
  async startGameAsyncInner() {
    this.trackTimeTo("startGameAsync");
    await this._prepareEntryData();
  }
  _getEntryPointDataForce() {
    return this.entryData;
  }
  async _createEntryPromise() {
    await Promise.all([
      this.getFriendsAsync().then(() => {
        this.trackTimeTo("getFriendsAsync");
      }),
      this.canSubscribeBotAsync().then(() => {
        this.trackTimeTo("canSubscribeBotAsync");
      }),
      this.canCreateShortcutAsync().then(() => {
        this.trackTimeTo("canCreateShortcutAsync");
      }),
      this.checkCanPlayerMatchAsync().then(() => {
        this.trackTimeTo("checkCanPlayerMatchAsync");
      }),
      this.getEntryPointAsync().then(() => {
        this.trackTimeTo("getEntryPointAsync");
      }),
      this.getTournamentAsync().then((tournament) => {
        this.entryContextTournament = tournament;
      }).catch(() => null).then(() => void this.trackTimeTo("getTournamentAsync"))
    ]);
  }
  async getFriendsAsync() {
    try {
      await this.getPlayersAsync();
      this.resolveFriendsAvailable();
    } catch (error) {
      this.rejectFriendsAvailable(error);
    }
    return this.friends;
  }
  async getPlayersAsync() {
    if (this.friends.length > 0) {
      return this.friends;
    }
    const addFriend = (id, name, photo) => {
      const friend = new Friend([id, name, photo || DEFAULT_PLAYER_PHOTO_URL]);
      friend.isPlayer = true;
      this.friendIds.push(friend.id);
      this.friends.push(friend);
      this.friendsMap[id] = friend;
    };
    const queryParams = getQueryParameters();
    if (queryParams.friends) {
      const friendIds = queryParams.friends.split(",");
      for (const friendId of friendIds) {
        if (friendId === this.playerID) {
          throw Error(
            `Invalid friends query parameter value ${friendId}: cannot equal player ID`
          );
        }
        const mockFriend = mockConfig.player[friendId];
        const friendArgs = mockFriend ? [friendId, mockFriend.name, mockFriend.photo] : [friendId, friendId, DEFAULT_PLAYER_PHOTO_URL];
        addFriend(...friendArgs);
      }
      return this.friends;
    }
    for (const friendId in mockConfig.player) {
      if (friendId !== this.playerID) {
        const mockFriend = mockConfig.player[friendId];
        addFriend(friendId, mockFriend.name, mockFriend.photo);
      }
    }
    const loggedInAsMockFriend = this.playerID in mockConfig.player;
    const previousPlayerId = getLocalStorageData(LOCAL_STORAGE_PLAYER_ID_KEY);
    if (loggedInAsMockFriend && previousPlayerId && typeof previousPlayerId === "string") {
      addFriend(previousPlayerId, "Test Name " + previousPlayerId);
    }
    return this.friends;
  }
  getSignedPlayerInfoAsync(requestPayload) {
    const generateMockSignature = () => [
      btoa(JSON.stringify({ alg: "HS256", typ: "JWT" })),
      btoa(
        JSON.stringify({ issued_at: Math.floor(globalTime.getNow() / 1e3) })
        // Compatible with FBInstant token payload
      ),
      btoa("mock-signature")
    ].join(".");
    const signedPlayerInfo = new SignedPlayerInfo();
    signedPlayerInfo.payload = requestPayload;
    signedPlayerInfo.playerId = this.playerID;
    signedPlayerInfo.signature = this._gameConfig.mockSignature || generateMockSignature();
    return Promise.resolve(signedPlayerInfo);
  }
  isContextSizeBetween(min, max) {
    if (!this._context) {
      return null;
    }
    if (max === null) {
      max = Infinity;
    }
    const result = { answer: false, minSize: min, maxSize: max };
    const playerCount = this._context.players.length;
    result.answer = playerCount >= min && playerCount <= max;
    return result;
  }
  async chooseContextAsync(_opts) {
    this.beforeContextSwitchCallbacks.forEach((callback) => callback());
    const selection = await friendPicker.open(this);
    const context = selection.type === "single" ? VirtualContext.single(selection.friend, this.playerID) : selection.type === "group" ? VirtualContext.group(selection.players, selection.id) : selection.type === "tournament" ? VirtualContext.tournament(selection.id) : unreachable(selection);
    this.updateContext("ChooseAsync", context);
    if (this.contextID && this.contextID.startsWith("tournament")) {
      await this.getTournamentAsync();
    }
  }
  async createContextAsync(playerID) {
    this.beforeContextSwitchCallbacks.forEach((callback) => callback());
    const friend = await contextSwitcher.open(playerID, this);
    if (!friend) {
      throw createError("INVALID_PARAM");
    }
    const context = VirtualContext.single(friend, this.playerID);
    if (this.contextID === context.id) {
      throw createError("SAME_CONTEXT");
    }
    this.updateContext("CreateAsync", context);
  }
  switchContextAsync(contextID) {
    this.beforeContextSwitchCallbacks.forEach((callback) => callback());
    return switchAsync.open(contextID, this).then((context) => {
      this.updateContext("SwitchAsync", context);
      if (this.contextID && this.contextID.startsWith("tournament")) {
        return this.getTournamentAsync().then(() => null);
      }
      return null;
    });
  }
  checkCanPlayerMatchAsync() {
    return Promise.resolve(true);
  }
  matchPlayerAsync(_matchTag, _switchContextWhenMatched, _offlineMatch) {
    this.beforeContextSwitchCallbacks.forEach((callback) => callback());
    return matchmakingInfo.open().then(() => matchmakingSearch.open()).then((context) => {
      this.updateContext("MatchPlayerAsync", context);
      return null;
    });
  }
  getContextPlayersAsync() {
    if (!this._context) {
      return Promise.reject(new Error("INVALID_OPERATION"));
    }
    return Promise.resolve(this._context.players);
  }
  setSessionData(_data) {
  }
  purchaseAsync(productID, payload) {
    if (!this._onProvisionProductAsync) {
      return Promise.reject(new Error(Platform.ERROR_NO_PROVISION));
    }
    const product = this._getProduct(productID);
    if (!product) {
      console.log(
        `Product ${productID} not found - did you populate Platform.catalog?`
      );
      return Promise.resolve();
    }
    const purchaseData = {
      developerPayload: payload,
      // A developer-specified string, provided during the purchase of the product
      paymentID: "",
      // The identifier for the purchase transaction
      productID,
      // The product's game-specified identifier
      purchaseTime: Math.floor(Date.now() / 1e3).toString(),
      // Unix timestamp of when the purchase occurred
      purchaseToken: Math.random().toString(36).substring(7),
      // A token representing the purchase that may be used to consume the purchase
      signedRequest: ""
      // Server-signed encoding of the purchase request
    };
    purchaseData.signedRequest = JSON.stringify(purchaseData);
    const purchase = purchaseFromLocalCurrencyPurchase(
      localCurrencyPurchaseFromFacebookData(purchaseData),
      product,
      (...args) => this.convertCurrency(...args)
    );
    return purchaseConfirmation.open(product).then(
      () => this._onProvisionProductAsync(purchase, product, {
        consumedAtLaunch: false
      })
    ).then((result) => {
      return { purchase, product, result };
    });
  }
  shareAsync(opts) {
    console.log("GCInstant.shareAsync()", opts);
    this._setContextTournament(null);
    return shareAsyncDialog.open(opts.image);
  }
  async getSquadAsync(id) {
    let squadId = id;
    if (squadId.startsWith("squad:")) {
      squadId = squadId.substring(squadId.indexOf(":") + 1);
    }
    const squad = this.getSquadById(squadId);
    if (squad.getContextID() === this.contextID) {
      this._setContextSquad(squad);
    }
    return squad;
  }
  async createSquadAsync() {
    await squads.create();
    const squad = this.getSquadById(this.playerID);
    this.updateContext("CreateSquadAsync", VirtualContext.squad(squad.getID()));
    this._setContextSquad(squad);
    return squad;
  }
  postSessionScoreAsync(score) {
    if (score < 1 || !Number.isInteger(score)) {
      throw Error("Score must be a positive integer");
    }
    console.log("GCInstant.postSessionScoreAsync(", score, ")");
    if (this.contextID && this.contextID.startsWith("tournament")) {
      return this.getTournamentAsync().then(() => delay(500)).then(() => shareAsyncDialog.openTournamentShare());
    }
    return Promise.resolve().then(() => delay(500)).then(() => {
      const context = VirtualContext.tournament();
      this.updateContext("PostSessionScoreAsync", context);
    }).then(() => delay(500)).then(() => this.getTournamentAsync()).then(() => delay(500)).then(() => shareAsyncDialog.openTournamentShare());
  }
  async getTournamentAsync() {
    return new Promise((resolve, reject) => {
      const hoursFromUnix = Math.floor(globalTime.getNow() / 1e3 / 3600);
      if (!this.contextID || !this.contextID.startsWith("tournament")) {
        this._setContextTournament(null);
        reject(createError("TOURNAMENT_NOT_FOUND"));
        return;
      }
      const [_type, id] = this.contextID.split(":");
      const contextId = id ? id : "" + hoursFromUnix;
      this._setContextTournament({
        getContextID: () => "tournament:" + contextId,
        // Hours form unix timestamp
        getEndTime: () => hoursFromUnix * 3600 + 3600 * 2,
        // Hours from unix timestamp + 2 hours
        getTitle: () => "",
        getPayload: () => Promise.resolve(void 0)
      });
      resolve(this.contextTournament);
    });
  }
  async createTournamentAsync(opts) {
    console.log(
      "GCInstant.createTournamentAsync({",
      `initialScore: ${opts.initialScore},`,
      `title: ${opts.title}`,
      "})"
    );
    this.beforeContextSwitchCallbacks.forEach((callback) => callback());
    await delay(500);
    const context = VirtualContext.tournament();
    this.updateContext("CreateTournamentAsync", context);
    await delay(500);
    const tournament = await this.getTournamentAsync();
    await delay(500);
    await shareAsyncDialog.openTournamentShare();
    return tournament;
  }
  async shareTournamentAsync(opts) {
    console.log("GCInstant.shareTournamentAsync(", opts.score, ")");
    await delay(500);
    const tournament = await this.getTournamentAsync();
    await delay(500);
    await shareAsyncDialog.openTournamentShare();
    return tournament;
  }
  postTournamentScoreAsync(score, _analyticsData) {
    if (score < 1 || !Number.isInteger(score)) {
      throw Error("Score must be a positive integer");
    }
    console.log("GCInstant.postTournamentScoreAsync(", score, ")");
    if (this.contextID && this.contextID.startsWith("tournament")) {
      return this.getTournamentAsync().then(() => void delay(500));
    }
    return Promise.resolve().then(() => delay(500)).then(() => {
      const context = VirtualContext.tournament();
      this.updateContext("PostTournamentScoreAsync", context);
    }).then(() => delay(500)).then(() => this.getTournamentAsync()).then(() => void delay(500));
  }
  async createNativeBridge(_baseURL, _referralCode, _secret, _productIDs = []) {
    await delay(500);
  }
  async initializeNativeBridge(_baseURL, _bridgeSecret, _productIDs = []) {
    await delay(500);
  }
  //
  // Helpers.
  getSquadById(squadId) {
    const squad = {
      getContextID: () => `squad:${squadId}`,
      getID: () => squadId,
      getName: () => `Squad ${squadId}`,
      getImage: () => "https://storage.googleapis.com/devkit-instant/avatar/animal_0001.png",
      joinSquadAsync: async () => {
        await squads.join(squad.getID(), this.playerPhoto);
        this.updateContext("JoinSquadAsync", VirtualContext.squad(squadId));
        this._setContextSquad(squad);
      }
    };
    return squad;
  }
}
const PlatformMock$1 = PlatformMock;
export {
  PlatformMock,
  PlatformMock$1 as default
};
